import { useTranslation } from 'react-i18next';

import { bucketFor, toneForBucket } from '@/components/ui/scoreScale';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

interface Props {
  /** The rating on this question's score, or `null`/`undefined` for a question nobody rated. */
  value: number | null | undefined;
  /**
   * The id of the element holding the wording this mark is about — §9's pair rule. It becomes the
   * mark's accessible **description**, so the name stays the word (*"4, Above expectations"*) and a
   * screen reader landing on the mark still hears what was scored.
   */
  describedBy?: string;
  /**
   * §6.6 only: the value is already announced by the scale below, which is labelled by the same
   * question, so the mark is a **visual echo** and takes no second voice.
   */
  silent?: boolean;
}

/**
 * §6.6 and §6.7's per-question rating, as a mark in the **fixed-width left margin** at the head of
 * the question block (`DESIGN.md` §6.7, *"The rating sits in a fixed-width left margin"*).
 *
 * ⚠️ **It is a screen-local component and not a variant of `components/ui/Score`, deliberately.** §4's
 * rule is that anything used on one screen belongs in that screen's folder, and the mechanical half is
 * sharper: `Score` is what §6.2's histogram, §6.3's column and §6.5's card render, so a size prop or a
 * larger default on it would change three other screens to fix this one. The reason this rating was
 * ~12px in the first place is that `type.numeral` carries no size and `Score` inherits from wherever it
 * is placed.
 *
 * ⚠️ **The numeral carries the colour, and the hairline beneath it is gone here and only here.** At
 * 30px a 1px rule reads as an underline rather than as a judgement — §3 records the measurement this
 * answers. The tone is `toneForBucket`, which **is** §3's table, so this rendering cannot decide on its
 * own that a 2 is positive; and only the **negative** end takes colour, because five saturated numerals
 * down a forty-question page is the wall of colour §3 refuses in a table column, one screen later.
 *
 * ⚠️ **`—` for a question nobody rated, never a `0`** — a zero enters the reader's arithmetic. A
 * skipped question is information (§6.6), and on §6.6 the dash is also what shows the reader, down the
 * margin, which questions they have not answered yet.
 */
export function QuestionRating({ value, describedBy, silent }: Props) {
  const { t } = useTranslation('components');

  const unrated = value === null || value === undefined;
  const bucket = unrated ? undefined : bucketFor(value);

  return (
    <span
      /*
       * `aria-hidden` and an accessible name are alternatives and never both: an element hidden from
       * assistive technology with a name of its own carries a name nothing will ever read, which is
       * the kind of mistake that survives a review because it looks careful.
       */
      role={silent ? undefined : 'img'}
      aria-hidden={silent || undefined}
      aria-label={
        silent
          ? undefined
          : bucket === undefined
            ? t('score.none')
            : t('score.announce', { value: String(value), label: t(`score.${bucket}`) })
      }
      aria-describedby={silent ? undefined : describedBy}
      className={cn(
        type.marginScore,
        bucket === undefined
          ? 'text-ink-500'
          : toneForBucket[bucket] === 'negative'
            ? 'text-negative'
            : 'text-ink-900',
      )}
    >
      {unrated ? '—' : String(value)}
    </span>
  );
}
